import {
  CAPTURE_VARIANTS, RESEARCH_ENTRY_POINTS, RESEARCH_SCHEMA_VERSION, RESEARCH_SURFACES,
  isBrowserResearchEventName, parseResearchEnvelope, validRouteProperties, type BrowserResearchEventName, type CaptureVariant, type ResearchEntryPoint,
  type ResearchEventEnvelope, type ResearchProperties, type ResearchSurface,
} from '../../../server/src/research/schema.js';

declare const __LEARNIMMERSE_RELEASE__: string;

export interface ResearchStatus {
  schemaVersion: 1;
  eligible: boolean;
  /** An authenticated former participant may stop and clear earlier evidence. */
  canOptOut?: boolean;
  active: boolean;
  optedOut: boolean;
  /** Local-only record that a server opt-out has not yet been confirmed. */
  deletionUnconfirmed?: boolean;
  generationContentActive?: boolean;
  /** Stamped wording revision of the learner's generation-content consent (0 when never granted). */
  generationContentRevision?: number;
  /** Current wording revision the server requires; consent below it must be re-granted. */
  generationContentCurrentRevision?: number;
  subjectId?: string;
  consentRevision?: number;
  /** 'owner-default': the owner recorded this consent outside the app, so a fresh page needs no re-consent click. */
  consentSource?: 'owner-default' | 'explicit';
  release?: string;
  languagePair?: string;
  interfaceLanguage?: string;
  collection?: string[];
  neverCollected?: string[];
  replay?: {
    eligible: boolean;
    enabled: boolean;
    active: boolean;
    destination: string;
    region: string | null;
    retentionDays: number;
    unavailableReason?: string;
    generation?: string;
    /** Replay consent is the owner-recorded default and still on. */
    defaultOn?: boolean;
  };
}

export interface ResearchAttempt {
  sessionId: string;
  attemptId: string;
  surface: ResearchSurface;
  entryPoint: ResearchEntryPoint;
  captureVariant: CaptureVariant;
  /** Private local binding; it is never copied into an event or request body. */
  subjectId: string;
  /** Rotated on opt-out so a stored draft cannot resume an older consent period. */
  consentEpoch: string;
}

export interface ResearchStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ResearchDeliveryOptions {
  /** Injectable stores and clocks keep delivery journeys isolated and deterministic. */
  storage?: ResearchStorage | null;
  sessionStorage?: ResearchStorage | null;
  now?: () => number;
  schedule?: (callback: () => void, delayMs: number) => unknown;
  cancel?: (handle: unknown) => void;
  onlineTarget?: { addEventListener(type: 'online', listener: EventListener): void; removeEventListener(type: 'online', listener: EventListener): void } | null;
}

export const RESEARCH_QUEUE_MAX = 100;
export const RESEARCH_QUEUE_AGE_MS = 7 * 24 * 3600_000;
export const RESEARCH_RETRY_MAX = 8;
export const RESEARCH_RETRY_BASE_MS = 2_000;
export const RESEARCH_RETRY_MAX_MS = 5 * 60_000;
export const RESEARCH_BATCH_SIZE = 20;
const RESEARCH_REQUEST_TIMEOUT_MS = 15_000;
const RESEARCH_LOSS_COUNT_MAX = 1_000_000;

interface QueueEntry { event: ResearchEventEnvelope; queuedAt: number; retries: number; nextAttemptAt: number }
interface QueueLosses { dropped: number; expired: number; template: ResearchEventEnvelope | null }
interface QueueState { version: 1; events: QueueEntry[]; losses: QueueLosses }

const emptyQueue = (): QueueState => ({ version: 1, events: [], losses: { dropped: 0, expired: 0, template: null } });

function storageOrNull(kind: 'localStorage' | 'sessionStorage'): ResearchStorage | null {
  try { return typeof window === 'undefined' ? null : window[kind]; }
  catch { return null; }
}

function safeCount(value: unknown): number {
  return Number.isSafeInteger(value) && Number(value) > 0 ? Math.min(Number(value), RESEARCH_LOSS_COUNT_MAX) : 0;
}

function isSubjectId(value: unknown): value is string { return typeof value === 'string' && /^[0-9a-f]{32}$/.test(value); }

function isQueueLoss(event: ResearchEventEnvelope): boolean { return event.eventName === 'client.queue_loss'; }

export interface ResearchAttemptContext {
  attempt: ResearchAttempt;
  subjectId: string;
  lifecycle: number;
}

const uuid = (): string => {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) return '';
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((n) => n.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

function browserBuildRelease(): string {
  const value = typeof __LEARNIMMERSE_RELEASE__ === 'string' ? __LEARNIMMERSE_RELEASE__ : 'unknown';
  return /^[\w.+-]{1,80}$/.test(value) ? value : 'unknown';
}

/** One content-free sender. Telemetry requests never participate in capture or learning promises. */
export class ResearchDelivery {
  private statusValue: ResearchStatus = { schemaVersion: 1, eligible: false, active: false, optedOut: false };
  private subject = '';
  private suspendedSubject = '';
  private attempt: ResearchAttempt | null = null;
  private lifecycle = 0;
  private consentLifecycle = 0;
  private statusRead = 0;
  private session = '';
  private lastOccurredAt = 0;
  private attemptStartedAt: number | null = null;
  // An active server preference is not enough to activate a fresh page. The
  // learner must opt in during this page session so a failed opt-out cannot
  // be forgotten when browser storage was unavailable.
  private readonly activatedSubjects = new Set<string>();
  private readonly consentEpochs = new Map<string, string>();
  private readonly emittedOnce = new Set<string>();
  private readonly deletionStatusBySubject = new Map<string, { intent: string; revision: number; unconfirmed: boolean; ownerId: string | null }>();
  private ownerId: string | null = null;
  private readonly consentIntents = new Map<string, string>();
  private readonly pendingSends = new Set<AbortController>();
  private readonly channel: BroadcastChannel | null;
  private readonly fetcher: typeof fetch;
  private readonly storeOverride: ResearchStorage | null | undefined;
  private readonly sessionStoreOverride: ResearchStorage | null | undefined;
  private readonly now: () => number;
  private readonly schedule: (callback: () => void, delayMs: number) => unknown;
  private readonly cancel: (handle: unknown) => void;
  private readonly onlineTarget: ResearchDeliveryOptions['onlineTarget'];
  private listeningOnline = false;
  private retryTimer: unknown = null;
  private abortController: AbortController | null = null;
  private flushRun: Promise<void> | null = null;
  private stateSubject = '';
  private state: QueueState = emptyQueue();
  private readonly onOnline: EventListener = () => { void this.flush(); };
  private readonly onStorage = (event: StorageEvent): void => {
    const pendingDeletionSubject = this.subjectFromDeletionKey(event.key);
    if (pendingDeletionSubject) {
      const state = this.parseDeletionStatus(event.newValue);
      if (state) this.applyDeletionStatus(pendingDeletionSubject, state);
      return;
    }
    const intentSubject = this.subjectFromConsentIntentKey(event.key);
    if (intentSubject) {
      if (event.newValue !== event.oldValue) {
        this.consentLifecycle++;
        if (event.newValue === null) this.consentIntents.delete(intentSubject);
        else this.consentIntents.set(intentSubject, event.newValue);
      }
      return;
    }
    const subject = this.subjectFromOptOutKey(event.key);
    if (!subject) return;
    if (event.newValue === '1') this.stopForLocalOptOut(subject);
    // localStorage is shared, but sessionStorage is per tab. When another tab
    // explicitly re-consents, clear this tab's old suppression marker too.
    else if (event.newValue === null) {
      if (!this.clearSessionOptOut(subject)) return;
      if (this.statusValue.subjectId === subject && this.statusValue.optedOut) {
        this.activatedSubjects.add(subject);
        void this.start();
      }
    }
  };
  private readonly onBroadcast = (event: MessageEvent): void => {
    const data = event.data as { type?: unknown; subjectId?: unknown; intent?: unknown; revision?: unknown; unconfirmed?: unknown; ownerId?: unknown } | null;
    if (data?.type === 'opt-out' && typeof data.subjectId === 'string' && /^[0-9a-f]{32}$/.test(data.subjectId)) {
      this.stopForLocalOptOut(data.subjectId, false);
    } else if (data?.type === 'deletion-status' && typeof data.subjectId === 'string' &&
        /^[0-9a-f]{32}$/.test(data.subjectId) && typeof data.intent === 'string' &&
        Number.isSafeInteger(data.revision) && typeof data.unconfirmed === 'boolean') {
      this.applyDeletionStatus(data.subjectId, {
        intent: data.intent, revision: data.revision as number, unconfirmed: data.unconfirmed,
        ownerId: typeof data.ownerId === 'string' ? data.ownerId : null,
      });
    }
  };

  constructor(fetcher: typeof fetch = fetch.bind(globalThis), options: ResearchDeliveryOptions = {}) {
    this.fetcher = fetcher;
    this.storeOverride = options.storage;
    this.sessionStoreOverride = options.sessionStorage;
    this.now = options.now ?? (() => Date.now());
    this.schedule = options.schedule ?? ((callback, delayMs) => setTimeout(callback, delayMs));
    this.cancel = options.cancel ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.onlineTarget = options.onlineTarget === undefined
      ? (typeof window === 'undefined' ? null : window)
      : options.onlineTarget;
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage);
      let channel: BroadcastChannel | null = null;
      try {
        if (typeof window.BroadcastChannel === 'function') channel = new window.BroadcastChannel('li-research-opt-out-v1');
      } catch { /* storage events and send-time checks still suppress collection */ }
      this.channel = channel;
      this.channel?.addEventListener('message', this.onBroadcast);
    } else this.channel = null;
    this.loadUnconfirmedDeletionSubjects();
  }

  get status(): ResearchStatus { return this.statusValue; }
  currentAttempt(): ResearchAttempt | null { return this.attempt; }

  captureAttemptContext(attempt: ResearchAttempt | null = this.attempt): ResearchAttemptContext | null {
    const subjectId = attempt?.subjectId;
    if (!attempt || !this.statusValue.active || typeof subjectId !== 'string' || !this.subject ||
        !this.attemptAllowed(attempt)) return null;
    if (this.hasLocalOptOut(subjectId)) {
      this.stopForLocalOptOut(subjectId);
      return null;
    }
    return { attempt, subjectId: this.subject, lifecycle: this.lifecycle };
  }

  completeAttempt(attempt: ResearchAttempt): void {
    if (this.attempt?.attemptId === attempt.attemptId && this.attempt.sessionId === attempt.sessionId) {
      this.attempt = null;
      this.attemptStartedAt = null;
    }
  }

  /** Stable per-tab session join key shared with the optional visible-screen replay adapter. */
  sessionId(): string { return this.statusValue.active ? this.getSession() : ''; }

  async start(): Promise<ResearchStatus> {
    const lifecycle = this.lifecycle;
    const statusRead = ++this.statusRead;
    try {
      const response = await this.fetcher('/api/research/status', { credentials: 'same-origin', headers: { accept: 'application/json' } });
      if (!response.ok) return this.statusValue;
      const serverStatus = await response.json() as ResearchStatus;
      const hasConsentAccess = serverStatus.eligible || serverStatus.canOptOut === true;
      const subject = hasConsentAccess && typeof serverStatus.subjectId === 'string' ? serverStatus.subjectId : '';
      const next: ResearchStatus = {
        ...serverStatus,
        deletionUnconfirmed: subject ? this.hasUnconfirmedDeletion(subject) : false,
      };
      const replay = next.replay;
      if (next.schemaVersion !== 1 || typeof next.eligible !== 'boolean' || typeof next.active !== 'boolean' ||
          (next.canOptOut !== undefined && typeof next.canOptOut !== 'boolean') ||
          (hasConsentAccess && (!Number.isSafeInteger(next.consentRevision) || (next.consentRevision ?? -1) < 0)) ||
          (replay !== undefined && (typeof replay.eligible !== 'boolean' || typeof replay.enabled !== 'boolean' ||
            typeof replay.active !== 'boolean' || typeof replay.destination !== 'string' ||
            !(replay.region === null || typeof replay.region === 'string') ||
            !Number.isSafeInteger(replay.retentionDays) || replay.retentionDays < 1 || replay.retentionDays > 90 ||
            (replay.generation !== undefined && (typeof replay.generation !== 'string' || !/^[0-9a-f-]{36}$/i.test(replay.generation))))) ||
          lifecycle !== this.lifecycle || statusRead !== this.statusRead) return this.statusValue;
      const priorSubject = this.subject || this.suspendedSubject;
      if (subject && priorSubject && subject !== priorSubject) this.retireQueue(priorSubject);
      if (next.active && subject && this.hasLocalOptOut(subject)) {
        this.subject = subject;
        this.statusValue = next;
        this.stopForLocalOptOut(subject);
        return this.statusValue;
      }
      // An owner-recorded default is the one consent a fresh page may inherit: the local stop marker was checked
      // just above, and the learner's explicit stop turns the server row to 'explicit'/inactive.
      if (next.active && subject && next.consentSource !== 'owner-default' && !this.activatedSubjects.has(subject)) {
        // Do not treat an old server-side active flag as consent for this new
        // page. This is the recovery boundary when an opt-out could not be
        // persisted because browser storage failed.
        this.attempt = null;
        this.subject = '';
        this.statusValue = { ...next, active: false };
        this.dispatchStateChange();
        return this.statusValue;
      }
      if (subject && next.optedOut) this.clearQueue(subject);
      if (!next.active || subject !== this.subject) this.attempt = null;
      this.subject = next.active ? subject : '';
      if (subject) this.suspendedSubject = '';
      this.statusValue = next;
      if (next.active && subject) {
        if (this.stateSubject !== subject) {
          this.stateSubject = subject;
          this.state = this.readQueue(subject);
        }
        this.prune(this.state);
        this.persistQueue(subject, this.state);
        this.listenForOnline();
        void this.flush();
      }
      this.dispatchStateChange();
    } catch { /* status failures leave collection stopped */ }
    return this.statusValue;
  }

  async optIn(): Promise<boolean> {
    const lifecycle = this.lifecycle;
    const consentLifecycle = this.consentLifecycle;
    try {
      const consent = await this.readConsentState();
      if (!consent || lifecycle !== this.lifecycle || consentLifecycle !== this.consentLifecycle ||
          (this.statusValue.subjectId && this.statusValue.subjectId !== consent.subjectId)) return false;
      if (!this.statusValue.subjectId) {
        this.statusValue = { ...this.statusValue, eligible: true, subjectId: consent.subjectId, consentRevision: consent.consentRevision };
      }
      const intent = this.beginConsentIntent(consent.subjectId);
      if (!intent) return false;
      const operationConsentLifecycle = this.consentLifecycle;
      let response: Response | null = null;
      if (!this.isConsentIntentCurrent(consent.subjectId, intent) || lifecycle !== this.lifecycle ||
          operationConsentLifecycle !== this.consentLifecycle) return false;
      response = await this.fetcher('/api/research/participation', {
        method: 'POST', credentials: 'same-origin',
        headers: {
          'content-type': 'application/json', 'sec-fetch-site': 'same-origin',
          'X-Research-Subject': consent.subjectId,
        },
        body: JSON.stringify({ consentRevision: consent.consentRevision }),
      });
      if (!response?.ok || lifecycle !== this.lifecycle || operationConsentLifecycle !== this.consentLifecycle ||
          !this.isConsentIntentCurrent(consent.subjectId, intent)) return false;
      const enrolled = await response.json().catch(() => null) as { subjectId?: unknown } | null;
      if (enrolled?.subjectId !== consent.subjectId || lifecycle !== this.lifecycle ||
          operationConsentLifecycle !== this.consentLifecycle || !this.isConsentIntentCurrent(consent.subjectId, intent) ||
          !this.clearLocalOptOut(consent.subjectId)) return false;
      this.activatedSubjects.add(consent.subjectId);
      await this.start();
      const active = lifecycle === this.lifecycle && this.statusValue.active &&
        operationConsentLifecycle === this.consentLifecycle && this.statusValue.subjectId === consent.subjectId &&
        this.isConsentIntentCurrent(consent.subjectId, intent);
      if (!active) this.activatedSubjects.delete(consent.subjectId);
      return active;
    } catch { return false; }
  }

  /** The replay adapter shares the current semantic consent boundary and subject. */
  setReplayActiveLocal(active: boolean, generation?: string): void {
    if (!this.statusValue.replay) return;
    const nextActive = active && this.statusValue.active && this.statusValue.replay.enabled;
    const nextGeneration = generation ?? (active ? this.statusValue.replay.generation : undefined);
    // The adapter calls this on every stop path, including on paths that end
    // in a replay-controls render; dispatching unconditionally re-enters
    // replay.start() through li-research-state synchronously, once per call.
    if (this.statusValue.replay.active === nextActive && this.statusValue.replay.generation === nextGeneration) return;
    this.statusValue = {
      ...this.statusValue,
      replay: { ...this.statusValue.replay, active: nextActive, generation: nextGeneration },
    };
    this.dispatchStateChange();
  }

  async optOut(): Promise<boolean> {
    const previousStatus = this.statusValue;
    const previousSubject = this.statusValue.subjectId;
    const intent = previousSubject ? this.beginConsentIntent(previousSubject) : null;
    this.suspend();
    if (previousSubject) {
      this.rotateConsentEpoch(previousSubject);
      this.setLocalOptOut(previousSubject);
      this.clearQueue(previousSubject);
    }
    this.statusValue = { ...previousStatus, active: false, optedOut: true };
    this.dispatchStateChange();
    if (!previousSubject || !intent) return true;
    return this.confirmServerOptOut(previousSubject, intent, this.lifecycle, this.consentLifecycle);
  }

  /** Separate, reversible permission for the owner to inspect pack and exam content and provenance. */
  async setGenerationContentAccess(enabled: boolean): Promise<boolean> {
    const subject = this.statusValue.subjectId;
    if (!this.statusValue.active || !subject) return false;
    try {
      const response = await this.fetcher('/api/research/generation-content', {
        method: enabled ? 'POST' : 'DELETE', credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'sec-fetch-site': 'same-origin', 'X-Research-Subject': subject }, body: '{}',
      });
      if (!response.ok) {
        // A stop answered 503 may still have been applied (the server ends the scope, then reports the journal
        // failure), so read the real state instead of leaving the control showing it on.
        if (!enabled && response.status === 503) await this.start();
        return false;
      }
      await this.start();
      return this.statusValue.generationContentActive === enabled;
    } catch { return false; }
  }

  /** Stop local collection synchronously when the signed-in account changes. */
  suspend(ownerId: string | null = this.ownerId): void {
    if (this.subject) this.suspendedSubject = this.subject;
    this.lifecycle++;
    this.consentLifecycle++;
    this.ownerId = ownerId;
    this.clearRetryTimer();
    this.abortController?.abort();
    this.abortController = null;
    this.abortPendingSends();
    this.attempt = null;
    this.attemptStartedAt = null;
    this.session = '';
    this.lastOccurredAt = 0;
    try { this.sessionStore()?.removeItem('li-research-session-v1'); } catch { /* storage may be disabled */ }
    this.subject = '';
    this.statusValue = {
      schemaVersion: 1, eligible: false, active: false, optedOut: false,
      deletionUnconfirmed: [...this.deletionStatusBySubject.values()].some((state) =>
        state.unconfirmed && state.ownerId !== null && state.ownerId === ownerId),
    };
    this.dispatchStateChange();
  }

  beginAttempt(input: { surface: ResearchSurface; entryPoint: ResearchEntryPoint; captureVariant: CaptureVariant }): ResearchAttempt | null {
    const subject = this.statusValue.subjectId;
    if (this.statusValue.active && typeof subject === 'string' && this.hasLocalOptOut(subject)) {
      this.stopForLocalOptOut(subject);
    }
    if (!this.statusValue.active || !this.subject ||
        !(RESEARCH_SURFACES as readonly string[]).includes(input.surface) ||
        !(RESEARCH_ENTRY_POINTS as readonly string[]).includes(input.entryPoint) ||
        !(CAPTURE_VARIANTS as readonly string[]).includes(input.captureVariant)) return null;
    const sessionId = this.getSession();
    const attemptId = uuid();
    if (!sessionId || !attemptId) return null;
    this.attempt = { sessionId, attemptId, ...input, subjectId: this.subject, consentEpoch: this.consentEpochFor(this.subject) };
    this.attemptStartedAt = this.monotonicNow();
    return this.attempt;
  }

  headers(attempt: ResearchAttempt | null = this.attempt): Record<string, string> {
    if (attempt && this.statusValue.active && attempt.subjectId === this.statusValue.subjectId &&
        this.hasLocalOptOut(attempt.subjectId)) {
      const subjectId = attempt.subjectId;
      this.stopForLocalOptOut(subjectId);
      return {};
    }
    return this.attemptAllowed(attempt) ? {
      'X-Research-Subject': attempt.subjectId,
      'X-Research-Session': attempt.sessionId,
      'X-Research-Attempt': attempt.attemptId,
      'X-Research-Variant': attempt.captureVariant,
      'X-Research-Surface': attempt.surface,
      'X-Research-Entry': attempt.entryPoint,
    } : {};
  }

  emit(eventName: BrowserResearchEventName, properties: ResearchProperties = {}): void {
    this.emitFor(this.attempt, eventName, properties);
  }

  /** Emit against a persisted draft/landing attempt only while its consent binding still matches. */
  emitFor(attempt: ResearchAttempt | null, eventName: BrowserResearchEventName, properties: ResearchProperties = {}): void {
    if (!attempt || !this.attemptAllowed(attempt) || !isBrowserResearchEventName(eventName) ||
        !this.validProperties(eventName, properties)) return;
    const status = this.statusValue;
    const event: ResearchEventEnvelope = {
      schemaVersion: RESEARCH_SCHEMA_VERSION,
      eventId: uuid(), eventName, subjectId: attempt.subjectId,
      sessionId: attempt.sessionId, attemptId: attempt.attemptId,
      occurredAt: this.nextOccurredAt(),
      ...(this.attemptStartedAt !== null && this.attempt?.attemptId === attempt.attemptId ? {
        clientElapsedMs: Math.max(0, this.monotonicNow() - this.attemptStartedAt),
      } : {}),
      release: browserBuildRelease(), surface: attempt.surface, entryPoint: attempt.entryPoint,
      languagePair: status.languagePair!, interfaceLanguage: status.interfaceLanguage!,
      captureVariant: attempt.captureVariant, properties,
    };
    if (event.eventId) this.enqueue(event, this.lifecycle, attempt.subjectId, attempt);
  }

  /** Re-rendered interface reasons are recorded once for an attempt and stable reason. */
  emitOnce(key: string, eventName: BrowserResearchEventName, properties: ResearchProperties = {}, attempt: ResearchAttempt | null = this.attempt): void {
    if (!attempt) return;
    const compound = `${attempt.sessionId}:${attempt.attemptId}:${key}`;
    if (this.emittedOnce.has(compound)) return;
    this.emittedOnce.add(compound);
    if (this.emittedOnce.size > 1000) this.emittedOnce.delete(this.emittedOnce.values().next().value!);
    this.emitFor(attempt, eventName, properties);
  }

  /** Emit only while the attempt, account, and lifecycle captured at submission are still current. */
  emitForContext(context: ResearchAttemptContext, eventName: BrowserResearchEventName, properties: ResearchProperties = {}): void {
    const status = this.statusValue;
    const subjectId = status.subjectId;
    if (!this.isContextCurrent(context) || typeof subjectId !== 'string' || !/^[0-9a-f]{32}$/.test(subjectId) ||
        !status.languagePair || !status.interfaceLanguage || !isBrowserResearchEventName(eventName)) return;
    if (!this.validProperties(eventName, properties)) return;
    const event: ResearchEventEnvelope = {
      schemaVersion: RESEARCH_SCHEMA_VERSION,
      eventId: uuid(), eventName, subjectId: context.subjectId,
      sessionId: context.attempt.sessionId, attemptId: context.attempt.attemptId,
      occurredAt: this.nextOccurredAt(),
      // The elapsed clock belongs to the live attempt only: a captured context
      // from an earlier attempt must not borrow the current attempt's timing.
      ...(this.attemptStartedAt !== null && this.attempt?.attemptId === context.attempt.attemptId ? {
        clientElapsedMs: Math.max(0, this.monotonicNow() - this.attemptStartedAt),
      } : {}),
      release: browserBuildRelease(),
      surface: context.attempt.surface, entryPoint: context.attempt.entryPoint,
      languagePair: status.languagePair,
      interfaceLanguage: status.interfaceLanguage,
      captureVariant: context.attempt.captureVariant, properties,
    };
    if (!event.eventId) return;
    this.enqueue(event, context.lifecycle, context.subjectId, context.attempt);
  }

  private validProperties(eventName: BrowserResearchEventName, properties: ResearchProperties): boolean {
    if (Object.keys(properties).some((key) => !['reason', 'dropped', 'expired', 'hypothesis', 'task'].includes(key))) return false;
    if (!validRouteProperties(eventName, properties as Record<string, unknown>)) return false;
    if ((properties.dropped !== undefined || properties.expired !== undefined) && eventName !== 'client.queue_loss') return false;
    return true;
  }

  private attemptAllowed(attempt: ResearchAttempt | null): attempt is ResearchAttempt {
    const subjectId = this.statusValue.subjectId;
    if (!this.statusValue.active || !this.subject || !attempt ||
        typeof subjectId !== 'string' || subjectId !== attempt.subjectId || this.subject !== attempt.subjectId ||
        !/^[0-9a-f]{32}$/.test(attempt.subjectId) || !attempt.consentEpoch ||
        attempt.consentEpoch !== this.consentEpochFor(attempt.subjectId)) return false;
    if (this.hasLocalOptOut(subjectId)) {
      this.stopForLocalOptOut(subjectId);
      return false;
    }
    return !!this.statusValue.languagePair && !!this.statusValue.interfaceLanguage;
  }

  private isContextCurrent(context: ResearchAttemptContext): boolean {
    return this.lifecycle === context.lifecycle && this.subject === context.subjectId &&
      this.statusValue.subjectId === context.subjectId && this.attemptAllowed(context.attempt);
  }

  private nextOccurredAt(): string {
    const at = Math.max(this.now(), this.lastOccurredAt + 1);
    this.lastOccurredAt = at;
    return new Date(at).toISOString();
  }

  private monotonicNow(): number {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return Number.isFinite(now) ? now : Date.now();
  }

  private enqueue(event: ResearchEventEnvelope, lifecycle: number, subject: string, attempt: ResearchAttempt): void {
    if (lifecycle !== this.lifecycle || !this.statusValue.active || this.subject !== subject ||
        this.statusValue.subjectId !== subject || !this.attemptAllowed(attempt)) return;
    if (this.hasLocalOptOut(subject)) {
      this.stopForLocalOptOut(subject);
      return;
    }
    if (this.stateSubject !== subject) {
      this.stateSubject = subject;
      this.state = this.readQueue(subject);
    }
    this.prune(this.state);
    this.state.events.push({ event, queuedAt: this.now(), retries: 0, nextAttemptAt: this.now() });
    this.trimQueue(this.state);
    this.persistQueue(subject, this.state);
    void this.flush();
  }

  /** Flush the owner-scoped outbox without joining a capture or practice request. */
  async flush(): Promise<void> {
    this.clearRetryTimer();
    if (this.flushRun) return this.flushRun;
    if (!this.statusValue.active || !this.subject || this.stateSubject !== this.subject ||
        this.statusValue.subjectId !== this.subject || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    const lifecycle = this.lifecycle;
    const subject = this.subject;
    const run = this.drain(lifecycle, subject);
    this.flushRun = run.finally(() => { this.flushRun = null; });
    return this.flushRun;
  }

  /** Stop timers and in-flight delivery while retaining the owner-scoped queue across a restart. */
  dispose(): void {
    this.clearRetryTimer();
    this.abortController?.abort();
    this.abortController = null;
    if (this.listeningOnline && this.onlineTarget) this.onlineTarget.removeEventListener('online', this.onOnline);
    this.listeningOnline = false;
  }

  private async drain(lifecycle: number, subject: string): Promise<void> {
    while (lifecycle === this.lifecycle && subject === this.subject && this.statusValue.active) {
      const state = this.state;
      this.prune(state);
      this.ensureLossNotice(state);
      this.persistQueue(subject, state);
      const now = this.now();
      const due = state.events.filter((entry) => entry.nextAttemptAt <= now)
        .sort((a, b) => Number(isQueueLoss(b.event)) - Number(isQueueLoss(a.event)) || a.queuedAt - b.queuedAt);
      if (!due.length) { this.scheduleNext(state); return; }
      const batch = due.slice(0, RESEARCH_BATCH_SIZE);
      // Recheck the shared opt-out marker immediately before sending. A
      // storage event from another tab can arrive after flush was scheduled.
      if (this.hasLocalOptOut(subject)) {
        this.stopForLocalOptOut(subject);
        return;
      }
      const controller = new AbortController();
      this.abortController = controller;
      this.pendingSends.add(controller);
      const timeout = setTimeout(() => controller.abort(), RESEARCH_REQUEST_TIMEOUT_MS);
      let response: Response | null = null;
      try {
        response = await this.fetcher('/api/research/events', {
          method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: {
            'content-type': 'application/json', 'sec-fetch-site': 'same-origin',
            'X-Research-Session': batch[0].event.sessionId, 'X-Research-Attempt': batch[0].event.attemptId,
          },
          body: JSON.stringify({ events: batch.map((entry) => entry.event) }),
        });
      } catch { /* network, offline and timeout failures all use the same bounded retry path */ }
      finally {
        clearTimeout(timeout);
        if (this.abortController === controller) this.abortController = null;
        this.pendingSends.delete(controller);
      }
      if (lifecycle !== this.lifecycle || subject !== this.subject || this.stateSubject !== subject) return;
      if (response?.status === 202) {
        this.acknowledge(state, batch);
        this.persistQueue(subject, state);
        continue;
      }
      if (!response || response.status === 408 || response.status === 429 || response.status >= 500) {
        this.retry(state, batch, response);
        this.persistQueue(subject, state);
        this.scheduleNext(state);
        return;
      }
      this.refuse(state, batch);
      this.persistQueue(subject, state);
    }
  }

  private acknowledge(state: QueueState, batch: QueueEntry[]): void {
    const ids = new Set(batch.map((entry) => entry.event.eventId));
    state.events = state.events.filter((entry) => !ids.has(entry.event.eventId));
    for (const entry of batch) if (isQueueLoss(entry.event)) this.subtractLoss(state, entry.event.properties);
  }

  private retry(state: QueueState, batch: QueueEntry[], response: Response | null): void {
    const ids = new Set(batch.map((entry) => entry.event.eventId));
    const retryAfter = response?.status === 429 ? this.retryAfter(response) : 0;
    const exhausted = new Set<string>();
    for (const entry of state.events) {
      if (!ids.has(entry.event.eventId)) continue;
      if (entry.retries >= RESEARCH_RETRY_MAX) exhausted.add(entry.event.eventId);
      else {
        entry.retries++;
        const exponential = Math.min(RESEARCH_RETRY_MAX_MS, RESEARCH_RETRY_BASE_MS * 2 ** (entry.retries - 1));
        entry.nextAttemptAt = this.now() + Math.max(exponential, retryAfter);
      }
    }
    if (exhausted.size) {
      const removed = state.events.filter((entry) => exhausted.has(entry.event.eventId));
      state.events = state.events.filter((entry) => !exhausted.has(entry.event.eventId));
      for (const entry of removed) {
        if (!isQueueLoss(entry.event)) this.recordRemoved(state, [entry], 'dropped');
      }
    }
  }

  private refuse(state: QueueState, batch: QueueEntry[]): void {
    const ids = new Set(batch.map((entry) => entry.event.eventId));
    state.events = state.events.filter((entry) => !ids.has(entry.event.eventId));
    for (const entry of batch) {
      if (isQueueLoss(entry.event)) this.subtractLoss(state, entry.event.properties);
      else this.recordRemoved(state, [entry], 'dropped');
    }
    this.ensureLossNotice(state);
  }

  private retryAfter(response: Response): number {
    const value = response.headers.get('retry-after');
    if (!value) return 0;
    const seconds = Number(value);
    const until = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - this.now();
    return Number.isFinite(until) ? Math.max(0, Math.min(60 * 60_000, until)) : 0;
  }

  private prune(state: QueueState): void {
    const now = this.now();
    const expired = state.events.filter((entry) => now - entry.queuedAt > RESEARCH_QUEUE_AGE_MS);
    state.events = state.events.filter((entry) => now - entry.queuedAt <= RESEARCH_QUEUE_AGE_MS);
    this.recordRemoved(state, expired, 'expired');
  }

  private trimQueue(state: QueueState): void {
    while (state.events.length > RESEARCH_QUEUE_MAX) {
      let index = state.events.findIndex((entry) => !isQueueLoss(entry.event));
      if (index < 0) index = 0;
      const [removed] = state.events.splice(index, 1);
      if (removed && !isQueueLoss(removed.event)) this.recordRemoved(state, [removed], 'dropped');
    }
  }

  private recordRemoved(state: QueueState, entries: QueueEntry[], kind: 'dropped' | 'expired'): void {
    for (const entry of entries) {
      if (isQueueLoss(entry.event)) continue;
      state.losses[kind] = Math.min(RESEARCH_LOSS_COUNT_MAX, state.losses[kind] + 1);
      if (!state.losses.template) state.losses.template = entry.event;
    }
  }

  private ensureLossNotice(state: QueueState): void {
    const { dropped, expired, template } = state.losses;
    if ((!dropped && !expired) || !template || state.events.length >= RESEARCH_QUEUE_MAX || state.events.some((entry) => isQueueLoss(entry.event))) return;
    const eventId = uuid();
    if (!eventId) return;
    const properties: ResearchProperties = { ...(dropped ? { dropped } : {}), ...(expired ? { expired } : {}) };
    const event: ResearchEventEnvelope = {
      ...template, schemaVersion: RESEARCH_SCHEMA_VERSION, eventId, eventName: 'client.queue_loss',
      occurredAt: this.nextOccurredAt(), release: browserBuildRelease(), properties,
    };
    state.events.unshift({ event, queuedAt: this.now(), retries: 0, nextAttemptAt: this.now() });
  }

  private subtractLoss(state: QueueState, properties: ResearchProperties): void {
    state.losses.dropped = Math.max(0, state.losses.dropped - (properties.dropped ?? 0));
    state.losses.expired = Math.max(0, state.losses.expired - (properties.expired ?? 0));
    if (!state.losses.dropped && !state.losses.expired) state.losses.template = null;
  }

  private scheduleNext(state: QueueState): void {
    this.clearRetryTimer();
    if (!this.statusValue.active || !state.events.length) return;
    const next = Math.min(...state.events.map((entry) => entry.nextAttemptAt));
    this.retryTimer = this.schedule(() => { this.retryTimer = null; void this.flush(); }, Math.max(0, next - this.now()));
  }

  private clearRetryTimer(): void {
    if (this.retryTimer === null) return;
    this.cancel(this.retryTimer);
    this.retryTimer = null;
  }

  private listenForOnline(): void {
    if (this.listeningOnline || !this.onlineTarget) return;
    this.onlineTarget.addEventListener('online', this.onOnline);
    this.listeningOnline = true;
  }

  private queueKey(subject: string): string { return `li-research-v1:${subject}:queue`; }

  private readQueue(subject: string): QueueState {
    const state = emptyQueue();
    try {
      const raw = this.store()?.getItem(this.queueKey(subject));
      if (!raw || raw.length > 512 * 1024) return state;
      const parsed = JSON.parse(raw) as Partial<QueueState>;
      if (parsed.version !== 1 || !Array.isArray(parsed.events) || !parsed.losses || typeof parsed.losses !== 'object') return state;
      state.losses.dropped = safeCount(parsed.losses.dropped);
      state.losses.expired = safeCount(parsed.losses.expired);
      const template = parseResearchEnvelope(parsed.losses.template, new Date(this.now()));
      if (template && template.subjectId === subject && isBrowserResearchEventName(template.eventName)) state.losses.template = template;
      for (const rawEntry of parsed.events.slice(0, RESEARCH_QUEUE_MAX + 1)) {
        if (!rawEntry || typeof rawEntry !== 'object') continue;
        const candidate = rawEntry as Partial<QueueEntry>;
        const event = parseResearchEnvelope(candidate.event, new Date(this.now()));
        if (!event || event.subjectId !== subject || !isBrowserResearchEventName(event.eventName)) continue;
        if (!Number.isFinite(candidate.queuedAt) || Number(candidate.queuedAt) > this.now() + 5 * 60_000 ||
            !Number.isInteger(candidate.retries) || Number(candidate.retries) < 0 || Number(candidate.retries) > RESEARCH_RETRY_MAX ||
            !Number.isFinite(candidate.nextAttemptAt)) continue;
        state.events.push({ event, queuedAt: Number(candidate.queuedAt), retries: Number(candidate.retries), nextAttemptAt: Number(candidate.nextAttemptAt) });
      }
      if (!state.losses.template) state.losses.template = state.events.find((entry) => !isQueueLoss(entry.event))?.event ?? null;
      this.trimQueue(state);
    } catch { /* local storage can be disabled or partially written */ }
    return state;
  }

  private persistQueue(subject: string, state: QueueState): void {
    try { this.store()?.setItem(this.queueKey(subject), JSON.stringify(state)); }
    catch { /* delivery remains best-effort when device storage is unavailable */ }
  }

  private retireQueue(subject: string): void {
    const state = this.readQueue(subject);
    this.prune(state);
    const pending = state.events.filter((entry) => !isQueueLoss(entry.event));
    this.recordRemoved(state, pending, 'dropped');
    state.events = [];
    this.persistQueue(subject, state);
    if (this.stateSubject === subject) { this.state = state; this.stateSubject = ''; }
  }

  private clearQueue(subject: string): void {
    try { this.store()?.removeItem(this.queueKey(subject)); } catch { /* opt-out still disables sending in memory */ }
    if (this.stateSubject === subject) { this.stateSubject = ''; this.state = emptyQueue(); }
  }

  private store(): ResearchStorage | null {
    return this.storeOverride === undefined ? storageOrNull('localStorage') : this.storeOverride;
  }

  private sessionStore(): ResearchStorage | null {
    return this.sessionStoreOverride === undefined ? storageOrNull('sessionStorage') : this.sessionStoreOverride;
  }

  private async readConsentState(allowFormerParticipant = false): Promise<{ subjectId: string; consentRevision: number } | null> {
    try {
      const response = await this.fetcher('/api/research/status', {
        credentials: 'same-origin', headers: { accept: 'application/json' },
      });
      if (!response.ok) return null;
      const state = await response.json() as ResearchStatus;
      if (state.schemaVersion !== 1 ||
          !(state.eligible === true || (allowFormerParticipant && state.canOptOut === true)) ||
          typeof state.subjectId !== 'string' ||
          !/^[0-9a-f]{32}$/.test(state.subjectId) || !Number.isSafeInteger(state.consentRevision) ||
          (state.consentRevision ?? -1) < 0) return null;
      return { subjectId: state.subjectId, consentRevision: state.consentRevision! };
    } catch { return null; }
  }

  private consentIntentKey(subject: string): string { return `li-research-v1:${subject}:consent-intent`; }

  private readConsentIntent(subject: string): string | null {
    // A fresh local intent remains authoritative if storage rejects its write.
    // Other tabs update this cache through the storage event, and server
    // revision compare-and-swap remains the final ordering guard.
    const inMemory = this.consentIntents.get(subject);
    if (inMemory !== undefined) return inMemory;
    try {
      const shared = localStorage.getItem(this.consentIntentKey(subject));
      if (shared !== null) return shared;
    } catch { /* this tab may still have a session-scoped intent */ }
    try {
      const local = sessionStorage.getItem(this.consentIntentKey(subject));
      if (local !== null) return local;
    } catch { /* the in-memory value may still be available */ }
    return this.consentIntents.get(subject) ?? null;
  }

  private subjectFromConsentIntentKey(key: string | null): string | null {
    const match = key?.match(/^li-research-v1:([0-9a-f]{32}):consent-intent$/);
    return match?.[1] ?? null;
  }

  private beginConsentIntent(subject: string): string | null {
    const intent = uuid();
    if (!intent) return null;
    this.consentLifecycle++;
    this.consentIntents.set(subject, intent);
    try { localStorage.setItem(this.consentIntentKey(subject), intent); }
    catch { try { sessionStorage.setItem(this.consentIntentKey(subject), intent); } catch { /* server revision remains the ordering guard */ } }
    return intent;
  }

  private isConsentIntentCurrent(subject: string, intent: string): boolean {
    return this.readConsentIntent(subject) === intent;
  }

  private async confirmServerOptOut(
    subject: string,
    intent: string,
    lifecycle: number,
    consentLifecycle: number,
  ): Promise<boolean> {
    let confirmed = false;
    let revision = (this.statusValue.subjectId === subject ? this.statusValue.consentRevision ?? 0 : 0) + 1;
    let confirmedRevision = 0;
    if (lifecycle === this.lifecycle && consentLifecycle === this.consentLifecycle &&
        this.isConsentIntentCurrent(subject, intent)) {
      const consent = await this.readConsentState(true);
      if (consent && consent.subjectId === subject && lifecycle === this.lifecycle &&
          consentLifecycle === this.consentLifecycle && this.isConsentIntentCurrent(subject, intent)) {
        revision = consent.consentRevision + 1;
        try {
          const response = await this.fetcher('/api/research/participation', {
            method: 'DELETE', credentials: 'same-origin',
            headers: {
              'content-type': 'application/json', 'sec-fetch-site': 'same-origin',
              'X-Research-Subject': subject,
            },
            body: JSON.stringify({ consentRevision: consent.consentRevision }),
          });
          if (response.ok) {
            confirmed = true;
            const body = await response.json().catch(() => null) as { consentRevision?: unknown } | null;
            confirmedRevision = Number.isSafeInteger(body?.consentRevision)
              ? body!.consentRevision as number : revision;
          }
          // A revision conflict invalidates the explicit action captured above.
          // A new tap must read the latest revision before it can change consent.
        } catch { /* the local opt-out remains active and the warning stays visible */ }
      }
    }
    const intentCurrent = consentLifecycle === this.consentLifecycle && this.isConsentIntentCurrent(subject, intent);
    if (intentCurrent) {
      this.setDeletionUnconfirmed(subject, !confirmed, intent, confirmed ? confirmedRevision : revision);
    }
    if (lifecycle === this.lifecycle && this.statusValue.subjectId === subject &&
        consentLifecycle === this.consentLifecycle && this.isConsentIntentCurrent(subject, intent)) {
      this.statusValue = {
        ...this.statusValue,
        ...(confirmed ? { active: false, optedOut: true } : {}),
        ...(confirmed ? { consentRevision: confirmedRevision } : {}),
        deletionUnconfirmed: this.hasUnconfirmedDeletion(subject),
      };
      this.dispatchStateChange();
    }
    return confirmed;
  }

  private getSession(): string {
    if (this.session) return this.session;
    let id = '';
    try { id = this.sessionStore()?.getItem('li-research-session-v1') ?? ''; } catch { /* storage may be disabled */ }
    if (!id) id = uuid();
    this.session = id;
    try { this.sessionStore()?.setItem('li-research-session-v1', id); } catch { /* memory session is enough for this tab */ }
    return id;
  }

  private consentEpochFor(subject: string): string {
    const remembered = this.consentEpochs.get(subject);
    if (remembered) return remembered;
    const key = `li-research-v1:${subject}:epoch`;
    let value = '';
    try { value = this.store()?.getItem(key) ?? ''; } catch { /* storage failure cannot create a durable binding */ }
    if (!value) {
      value = uuid();
      try { this.store()?.setItem(key, value); } catch { /* page-local binding remains safe for this session */ }
    }
    if (value) this.consentEpochs.set(subject, value);
    return value;
  }

  private rotateConsentEpoch(subject: string): void {
    const value = uuid();
    if (!value) return;
    this.consentEpochs.set(subject, value);
    try { this.store()?.setItem(`li-research-v1:${subject}:epoch`, value); } catch { /* opt-out markers and lifecycle still suppress this page */ }
  }

  private optOutKey(subject: string): string { return `li-research-v1:${subject}:optout`; }

  private deletionUnconfirmedKey(subject: string): string { return `${this.optOutKey(subject)}:deletion-unconfirmed`; }

  private subjectFromDeletionKey(key: string | null): string | null {
    const match = key?.match(/^li-research-v1:([0-9a-f]{32}):optout:deletion-unconfirmed$/);
    return match?.[1] ?? null;
  }

  private hasUnconfirmedDeletion(subject: string): boolean {
    return this.readDeletionStatus(subject)?.unconfirmed ?? false;
  }

  private loadUnconfirmedDeletionSubjects(): void {
    for (const getStorage of [() => localStorage, () => sessionStorage]) {
      let storage: Storage;
      try { storage = getStorage(); } catch { continue; }
      try {
        for (let index = 0; index < storage.length; index++) {
          const subject = this.subjectFromDeletionKey(storage.key(index));
          const state = subject ? this.parseDeletionStatus(storage.getItem(this.deletionUnconfirmedKey(subject))) : null;
          if (subject && state) {
            const current = this.deletionStatusBySubject.get(subject);
            if (!current || this.shouldAcceptDeletionStatus(subject, current, state)) {
              this.deletionStatusBySubject.set(subject, state);
            }
          }
        }
      } catch { /* storage may be disabled or unavailable */ }
    }
  }

  private setDeletionUnconfirmed(subject: string, unconfirmed: boolean, intent: string, revision: number): void {
    const key = this.deletionUnconfirmedKey(subject);
    const state = { intent, revision, unconfirmed, ownerId: this.ownerId };
    const current = this.readDeletionStatus(subject);
    if (current && !this.shouldAcceptDeletionStatus(subject, current, state)) return;
    this.deletionStatusBySubject.set(subject, state);
    const encoded = JSON.stringify(state);
    const persist = (storage: Storage) => {
      try { storage.setItem(key, encoded); } catch { /* try the other storage scope */ }
    };
    try { persist(localStorage); } catch { /* local storage accessor may itself throw */ }
    try { persist(sessionStorage); } catch { /* session storage accessor may itself throw */ }
    this.applyDeletionStatus(subject, state);
    this.broadcastDeletionStatus(subject, state);
  }

  private applyDeletionStatus(subject: string, state: { intent: string; revision: number; unconfirmed: boolean; ownerId: string | null }): void {
    const current = this.deletionStatusBySubject.get(subject) ?? this.readDeletionStatus(subject);
    if (current && !this.shouldAcceptDeletionStatus(subject, current, state)) return;
    this.deletionStatusBySubject.set(subject, state);
    if (this.statusValue.subjectId === subject) {
      this.statusValue = { ...this.statusValue, deletionUnconfirmed: state.unconfirmed };
      this.dispatchStateChange();
    }
  }

  private broadcastDeletionStatus(subject: string, state: { intent: string; revision: number; unconfirmed: boolean }): void {
    try { this.channel?.postMessage({ type: 'deletion-status', subjectId: subject, ...state }); }
    catch { /* local page and storage-event updates still apply */ }
  }

  private readDeletionStatus(subject: string): { intent: string; revision: number; unconfirmed: boolean; ownerId: string | null } | null {
    let latest = this.deletionStatusBySubject.get(subject) ?? null;
    const key = this.deletionUnconfirmedKey(subject);
    for (const getStorage of [() => localStorage, () => sessionStorage]) {
      try {
        const state = this.parseDeletionStatus(getStorage().getItem(key));
        if (state && (!latest || this.shouldAcceptDeletionStatus(subject, latest, state))) latest = state;
      } catch { /* try this tab's other storage scope */ }
    }
    if (latest) this.deletionStatusBySubject.set(subject, latest);
    return latest;
  }

  private shouldAcceptDeletionStatus(
    subject: string,
    current: { intent: string; revision: number; unconfirmed: boolean; ownerId: string | null },
    incoming: { intent: string; revision: number; unconfirmed: boolean; ownerId: string | null },
  ): boolean {
    if (incoming.revision !== current.revision) return incoming.revision > current.revision;
    if (incoming.intent === current.intent) {
      // A confirmed deletion is final for this intent and server revision.
      // A slower concurrent request can fail after another tab confirmed the
      // same opt-out, but that failure must not resurrect the warning.
      return current.unconfirmed || !incoming.unconfirmed;
    }
    const latestIntent = this.readConsentIntent(subject);
    return incoming.intent === latestIntent && current.intent !== latestIntent;
  }

  private parseDeletionStatus(value: string | null): { intent: string; revision: number; unconfirmed: boolean; ownerId: string | null } | null {
    if (value === '1') return { intent: 'legacy', revision: 0, unconfirmed: true, ownerId: null };
    if (!value) return null;
    try {
      const parsed = JSON.parse(value) as { intent?: unknown; revision?: unknown; unconfirmed?: unknown; ownerId?: unknown };
      if (typeof parsed.intent === 'string' && Number.isSafeInteger(parsed.revision) &&
          typeof parsed.unconfirmed === 'boolean') {
        return { intent: parsed.intent, revision: parsed.revision as number, unconfirmed: parsed.unconfirmed,
          ownerId: typeof parsed.ownerId === 'string' && parsed.ownerId.length > 0 ? parsed.ownerId : null };
      }
    } catch { /* malformed local warning state is ignored */ }
    return null;
  }

  private subjectFromOptOutKey(key: string | null): string | null {
    const match = key?.match(/^li-research-v1:([0-9a-f]{32}):optout$/);
    return match?.[1] ?? null;
  }

  private hasLocalOptOut(subject: string): boolean {
    try {
      if (localStorage.getItem(this.optOutKey(subject)) === '1' ||
          sessionStorage.getItem(this.optOutKey(subject)) === '1') return true;
      // A tab must be able to persist a shared marker before it participates.
      // This also keeps a reload inactive after an opt-out write was rejected.
      const probe = 'li-research-v1:' + subject + ':storage-probe';
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      return false;
    } catch {
      // When the browser cannot establish that the learner has not opted out,
      // collection stays off. In particular, storage failures are not consent.
      return true;
    }
  }

  private setLocalOptOut(subject: string): void {
    this.persistLocalOptOut(subject);
    this.broadcastOptOut(subject);
  }

  private persistLocalOptOut(subject: string): void {
    try { localStorage.setItem(this.optOutKey(subject), '1'); } catch { /* session storage is the reload fallback */ }
    try { sessionStorage.setItem(this.optOutKey(subject), '1'); } catch { /* the active page still fails closed */ }
  }

  private stopForLocalOptOut(subject: string, propagate = true): void {
    // Invalidate this page's explicit-consent grant even if another path has
    // already stopped it. A later status refresh must never reuse that grant.
    this.activatedSubjects.delete(subject);
    const previousStatus = this.statusValue;
    if (previousStatus.subjectId !== subject) return;
    this.rotateConsentEpoch(subject);
    // A tab can discover an opt-out marker, or a storage error, before the
    // originating tab has made that suppression durable everywhere possible.
    this.persistLocalOptOut(subject);
    this.clearRetryTimer();
    this.abortController?.abort();
    this.abortController = null;
    this.clearQueue(subject);
    this.consentLifecycle++;
    this.lifecycle++;
    this.abortPendingSends();
    this.attempt = null;
    this.attemptStartedAt = null;
    this.session = '';
    this.lastOccurredAt = 0;
    try { this.sessionStore()?.removeItem('li-research-session-v1'); } catch { /* storage may be disabled */ }
    this.subject = '';
    this.statusValue = {
      ...previousStatus, active: false, optedOut: true,
      deletionUnconfirmed: this.hasUnconfirmedDeletion(subject),
    };
    this.dispatchStateChange();
    if (propagate) this.broadcastOptOut(subject);
    if (previousStatus.active) void this.disableServerParticipation(subject, this.lifecycle);
  }

  private broadcastOptOut(subject: string): void {
    try { this.channel?.postMessage({ type: 'opt-out', subjectId: subject }); } catch { /* local suppression still applies */ }
  }

  private clearLocalOptOut(subject: string): boolean {
    try {
      localStorage.removeItem(this.optOutKey(subject));
      sessionStorage.removeItem(this.optOutKey(subject));
      return true;
    } catch { return false; }
  }

  private clearSessionOptOut(subject: string): boolean {
    try {
      sessionStorage.removeItem(this.optOutKey(subject));
      return true;
    } catch { return false; }
  }

  private abortPendingSends(): void {
    for (const controller of this.pendingSends) controller.abort();
    this.pendingSends.clear();
  }

  private async disableServerParticipation(subject: string, lifecycle: number): Promise<void> {
    if (lifecycle !== this.lifecycle || this.statusValue.subjectId !== subject) return;
    const intent = this.readConsentIntent(subject) ?? this.beginConsentIntent(subject);
    if (intent) void this.confirmServerOptOut(subject, intent, lifecycle, this.consentLifecycle);
  }

  private dispatchStateChange(): void {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('li-research-state'));
  }
}
